import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { getData, postData } from '../../../../services/apiService';
import {
    UserIcon,
    PhoneIcon,
    EnvelopeIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    TagIcon,
    DocumentTextIcon,
    ArrowPathIcon,
    EyeIcon,
    XMarkIcon,
    MagnifyingGlassIcon,
    FunnelIcon
} from '@heroicons/react/24/outline';

const CoordinatorDetails = () => {
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [organizerTypes, setOrganizerTypes] = useState([]);
    const [organizerStatus, setOrganizerStatus] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [organizerDetails, setOrganizerDetails] = useState([]);
    const [showOrganizerList, setShowOrganizerList] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');

    const {
        register,
        reset,
        handleSubmit,
        setValue,
        watch
    } = useForm({
        defaultValues: {
            organizer_name: '',
            organizer_type: '',
            Mobile_No: '',
            email: '',
            organizer_status: ''
        }
    });

    // Watch mobile for digit count
    const mobile = watch('Mobile_No');

    // Fetch organizers list
    const fetchOrganizers = async () => {
        try {
            setIsLoading(true);
            const response = await getData('/Event/GetOrganizer');

            if (!response?.IsError && Array.isArray(response?.Result)) {
                setOrganizerDetails(response.Result);
            } else {
                setOrganizerDetails([]);
            }
        } catch (error) {
            console.error('Error fetching organizer details:', error);
            setOrganizerDetails([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchOrganizers();
    }, []);

    // Fetch organizer types and status
    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                // Fetch organizer types
                const typesResponse = await getData('/Dropdown/GetOrganizerTypes');

                if (typesResponse && typesResponse.isError === false && typesResponse.data) {
                    const mappedTypes = typesResponse.data.map(item => ({
                        value: item.OrganizerType_ID.toString(),
                        label: item.OrganizerType_Name
                    }));
                    setOrganizerTypes(mappedTypes);
                } else {
                    // Fallback if API fails
                    setOrganizerTypes([]);
                }

                // Fetch organizer status
                const statusResponse = await getData('/Dropdown/GetOrganaizerStatus');

                if (statusResponse && statusResponse.isError === false && statusResponse.result) {
                    const mappedStatus = statusResponse.result.map(item => ({
                        value: item.Org_Status_ID.toString(),
                        label: item.Org_Status_Name
                    }));
                    setOrganizerStatus(mappedStatus);
                } else {
                    // Fallback if API fails
                    setOrganizerStatus([]);
                }
            } catch (error) {
                console.error('Error fetching dropdown data:', error);
                setOrganizerTypes([]);
                setOrganizerStatus([]);
            }
        };

        fetchDropdownData();
    }, []);

    // Handle mobile number input - allow only numbers
    const handleMobileNumberChange = (e) => {
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 10) {
            setValue('Mobile_No', value);
        }
    };

    // Validate form
    const validateForm = (data) => {
        const errors = {};

        // Required fields validation
        if (!data.organizer_name?.trim()) {
            errors.organizer_name = 'Organizer name is required';
        }

        if (!data.organizer_type?.trim()) {
            errors.organizer_type = 'Organizer type is required';
        }

        // Mobile number validation
        if (!data.Mobile_No?.trim()) {
            errors.Mobile_No = 'Mobile number is required';
        } else {
            const mobileNumber = data.Mobile_No.trim();
            if (!/^[0-9]+$/.test(mobileNumber)) {
                errors.Mobile_No = 'Mobile number must contain only digits';
            } else if (mobileNumber.length !== 10) {
                errors.Mobile_No = 'Mobile number must be exactly 10 digits';
            }
        }

        // Email validation
        if (!data.email?.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
            errors.email = 'Enter a valid email address';
        }

        if (!data.organizer_status?.trim()) {
            errors.organizer_status = 'Organizer status is required';
        }

        return errors;
    };

    // Handle form submission
    const onSubmit = async (data) => {
        // Validate form
        const validationErrors = validateForm(data);

        if (Object.keys(validationErrors).length > 0) {
            setFieldErrors(validationErrors);
            setErrorMessage('Please fix the errors in the form.');
            return;
        }

        // Clear errors if validation passes
        setFieldErrors({});
        setErrorMessage('');
        setIsSubmitting(true);

        try {
            const requestBody = {
                organizer_name: data.organizer_name.trim(),
                organizer_type: data.organizer_type,
                Mobile_No: data.Mobile_No.trim(),
                Email: data.email.trim(),
                organizer_status: data.organizer_status,
                ResultMessage: "string"
            };

            const response = await postData(
                '/Event/SaveOrganizer',
                requestBody
            );

            if (response && response.IsError == false) {
                setSuccessMessage(response.Message || 'Organizer details saved successfully!');
                setTimeout(() => setSuccessMessage(''), 5000);

                // Reset form
                reset({
                    organizer_name: '',
                    organizer_type: '',
                    Mobile_No: '',
                    email: '',
                    organizer_status: ''
                });
                setFieldErrors({});

                // Refresh the organizers list
                fetchOrganizers();
            } else {
                throw new Error(response?.message || 'Failed to save organizer details');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setErrorMessage(error.message || 'Failed to save organizer details. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle reset
    const handleReset = () => {
        reset({
            organizer_name: '',
            organizer_type: '',
            Mobile_No: '',
            email: '',
            organizer_status: ''
        });
        setSuccessMessage('');
        setErrorMessage('');
        setFieldErrors({});
    };

    // Filter organizers based on search and filter
    const filteredOrganizers = organizerDetails.filter(organizer => {
        const matchesSearch = searchTerm === '' ||
            organizer.organizer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            organizer.Email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            organizer.Mobile_No.includes(searchTerm);

        const matchesFilter = filterType === 'all' ||
            organizer.organizer_type === filterType;

        return matchesSearch && matchesFilter;
    });

    // Format date for display
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get status label
    const getStatusLabel = (statusId) => {
        const status = organizerStatus.find(s => s.value === statusId);
        return status ? status.label : statusId;
    };

    // Get type label
    const getTypeLabel = (typeId) => {
        const type = organizerTypes.find(t => t.value === typeId);
        return type ? type.label : typeId;
    };

    return (
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-3 md:p-4">
            <div className="w-full mx-auto">
                {/* Status Messages */}
                {successMessage && (
                    <div className="mb-4 p-3 bg-green-100 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2 text-green-800">
                            <CheckCircleIcon className="h-5 w-5" />
                            <span className="text-sm font-medium">{successMessage}</span>
                        </div>
                    </div>
                )}

                {errorMessage && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-200 rounded-lg">
                        <div className="flex items-center gap-2 text-red-800">
                            <ExclamationCircleIcon className="h-5 w-5" />
                            <span className="text-sm font-medium">{errorMessage}</span>
                        </div>
                    </div>
                )}

                {/* Header with View Button */}
                <div className="mb-6 flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Organizer Management</h1>
                        <p className="text-gray-600 mt-1">Add new organizers and manage existing ones</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowOrganizerList(true)}
                        className="px-4 py-2.5 bg-blue-700 text-white rounded-lg 
                            hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 
                            flex items-center gap-2 text-sm font-medium shadow-md hover:shadow-lg"
                    >
                        <EyeIcon className="h-4 w-4" />
                        View Organizers ({organizerDetails.length})
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" className="space-y-6">
                    <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
                        {/* Form Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-white/20 rounded-lg">
                                    <UserIcon className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-white">Add New Organizer</h2>
                                    <p className="text-blue-100 text-xs mt-0.5">
                                        Fill in the details below to register a new organizer
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Form Fields */}
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* Organizer Name */}
                                <div className="space-y-1.5">
                                    <label htmlFor="organizer_name" className="block text-xs font-medium text-gray-600">
                                        Organizer Name <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                                            <UserIcon className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            id="organizer_name"
                                            {...register('organizer_name')}
                                            className="pl-8 pr-3 py-2 text-sm w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="e.g., John Smith"
                                        />
                                    </div>
                                    {fieldErrors.organizer_name && (
                                        <p className="text-xs text-red-600 flex items-center gap-1">
                                            <ExclamationCircleIcon className="h-3 w-3" />
                                            {fieldErrors.organizer_name}
                                        </p>
                                    )}
                                </div>

                                {/* Organizer Type */}
                                <div className="space-y-1.5">
                                    <label htmlFor="organizer_type" className="block text-xs font-medium text-gray-600">
                                        Organizer Type <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                                            <TagIcon className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <select
                                            id="organizer_type"
                                            {...register('organizer_type')}
                                            className="pl-8 pr-6 py-2 text-sm w-full border border-gray-200 rounded-lg appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">Select organizer type</option>
                                            {organizerTypes.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                    {fieldErrors.organizer_type && (
                                        <p className="text-xs text-red-600 flex items-center gap-1">
                                            <ExclamationCircleIcon className="h-3 w-3" />
                                            {fieldErrors.organizer_type}
                                        </p>
                                    )}
                                </div>

                                {/* Mobile Number */}
                                <div className="space-y-1.5">
                                    <label htmlFor="Mobile_No" className="block text-xs font-medium text-gray-600">
                                        Mobile Number <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                                            <PhoneIcon className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="tel"
                                            id="Mobile_No"
                                            {...register('Mobile_No')}
                                            maxLength={10}
                                            onChange={handleMobileNumberChange}
                                            className="pl-8 pr-3 py-2 text-sm w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="1234567890"
                                            inputMode="numeric"
                                        />
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <div className="flex-1">
                                            {fieldErrors.Mobile_No && (
                                                <p className="text-xs text-red-600 flex items-center gap-1">
                                                    <ExclamationCircleIcon className="h-3 w-3" />
                                                    {fieldErrors.Mobile_No}
                                                </p>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {mobile?.length || 0}/10 digits
                                        </div>
                                    </div>
                                </div>

                                {/* Email Address */}
                                <div className="space-y-1.5">
                                    <label htmlFor="email" className="block text-xs font-medium text-gray-600">
                                        Email Address <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                                            <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="email"
                                            id="email"
                                            {...register('email')}
                                            className="pl-8 pr-3 py-2 text-sm w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="john@example.com"
                                        />
                                    </div>
                                    {fieldErrors.email && (
                                        <p className="text-xs text-red-600 flex items-center gap-1">
                                            <ExclamationCircleIcon className="h-3 w-3" />
                                            {fieldErrors.email}
                                        </p>
                                    )}
                                </div>

                                {/* Organizer Status */}
                                <div className="space-y-1.5">
                                    <label
                                        htmlFor="organizer_status"
                                        className="block text-xs font-medium text-gray-600"
                                    >
                                        Organizer Status <span className="text-red-500">*</span>
                                    </label>

                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                                            <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                                        </div>

                                        <select
                                            id="organizer_status"
                                            {...register('organizer_status')}
                                            className="pl-8 pr-8 py-2 text-sm w-full border border-gray-200 rounded-lg appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">Select Organizer Status</option>
                                            {organizerStatus.map((option) => (
                                                <option
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>

                                        <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                                            <svg
                                                className="h-4 w-4 text-gray-400"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </div>
                                    </div>

                                    {fieldErrors.organizer_status && (
                                        <p className="text-xs text-red-600 flex items-center gap-1">
                                            <ExclamationCircleIcon className="h-3 w-3" />
                                            {fieldErrors.organizer_status}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                            <div className="flex flex-col sm:flex-row gap-3 justify-end">
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    disabled={isSubmitting}
                                    className="px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 text-sm font-medium 
                                        hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 
                                        flex items-center justify-center gap-1.5 disabled:opacity-50"
                                >
                                    <ArrowPathIcon className="h-4 w-4" />
                                    Reset Form
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`
                                        px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 
                                        flex items-center justify-center gap-1.5
                                        ${isSubmitting
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
                                        }
                                    `}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircleIcon className="h-4 w-4" />
                                            Save Organizer Details
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            {/* Organizer List Modal */}
            {showOrganizerList && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        {/* Background overlay */}
                        <div
                            className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                            onClick={() => setShowOrganizerList(false)}
                        />

                        {/* Modal */}
                        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
                            {/* Modal Header */}
                            <div className="bg-blue-700 px-6 py-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-1.5 bg-white/20 rounded-lg">
                                            <EyeIcon className="h-5 w-5 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-white">Organizers List</h3>
                                            <p className="text-indigo-100 text-xs mt-0.5">
                                                Total {organizerDetails.length} organizers found
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowOrganizerList(false)}
                                        className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                                    >
                                        <XMarkIcon className="h-5 w-5 text-white" />
                                    </button>
                                </div>
                            </div>

                            {/* Search and Filter Bar */}
                            <div className="p-4 bg-gray-50 border-b border-gray-200">
                                <div className="flex justify-end">
                                    {/* Search Input */}
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Search by name, email or phone..."
                                            className="pl-9 pr-3 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Organizer Table */}
                            <div className="p-4 max-h-[60vh] overflow-y-auto">
                                {isLoading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <div className="text-center">
                                            <ArrowPathIcon className="h-8 w-8 animate-spin text-indigo-600 mx-auto" />
                                            <p className="mt-2 text-gray-600">Loading organizers...</p>
                                        </div>
                                    </div>
                                ) : filteredOrganizers.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="mx-auto h-12 w-12 text-gray-400 mb-3">
                                            <UserIcon className="h-12 w-12" />
                                        </div>
                                        <h3 className="text-sm font-medium text-gray-900 mb-1">No organizers found</h3>
                                        <p className="text-sm text-gray-500">
                                            {searchTerm || filterType !== 'all'
                                                ? 'Try adjusting your search or filter'
                                                : 'No organizers have been added yet'}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        ID
                                                    </th>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Organizer Name
                                                    </th>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Type
                                                    </th>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Contact
                                                    </th>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Last Updated
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {filteredOrganizers.map((organizer) => (
                                                    <tr key={organizer.organizer_id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                #{organizer.organizer_id}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {organizer.organizer_name}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                                                {getTypeLabel(organizer.organizer_type)}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="text-sm text-gray-900">{organizer.Email}</div>
                                                            <div className="text-xs text-gray-500">{organizer.Mobile_No}</div>
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${organizer.organizer_status === '1'
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-yellow-100 text-yellow-800'
                                                                }`}>
                                                                {getStatusLabel(organizer.organizer_status)}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                            {formatDate(organizer.updated_date ? organizer.updated_date : organizer.created_date)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Results Count */}
                                {filteredOrganizers.length > 0 && (
                                    <div className="mt-4 text-sm text-gray-500">
                                        Showing {filteredOrganizers.length} of {organizerDetails.length} organizers
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
                                <button
                                    onClick={() => setShowOrganizerList(false)}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
                                        transition-colors text-sm font-medium"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CoordinatorDetails;